import { useEffect, useEffectEvent, useRef, useState } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useAppDispatch } from '@app/store/store';
import type { NewInvestigationFormValues } from '@features/investigations/components/modals/add-new-investigation/investigation.schema';
import {
  useCreateTokenReservationMutation,
  useUpdateTokenReservationMutation,
} from '@features/token-management/api/token-management.api';
import type { GetTokenReservationPayload } from '@features/token-management/api/token-management.types';
import { cancelTrackedReservation, trackReservation } from '@features/token-management/utils/reservation-registry';
import type { RTKQueryError } from '@shared/utils/api.types';
import { isUnreachableError } from '@shared/utils/network-error.utils';

dayjs.extend(utc);

interface UseTokenReservationArgs {
  dateRange: NewInvestigationFormValues['dateRange'];
  assets: NewInvestigationFormValues['assets'];
}

const DEBOUNCED_INTERVAL_MS = 600;

export const useTokenReservation = ({ dateRange, assets }: UseTokenReservationArgs) => {
  const [reservationId, setReservationId] = useState<string | null>(null);
  const [estimatedTokens, setEstimatedTokens] = useState<number | null>(null);
  //used only to be able to disable submit button on the form
  const [error, setError] = useState<string | null>(null);
  // True for the whole debounce window: from the instant dateRange/assets change until the network
  // call actually starts. isCreating/isUpdating alone stay false during that window, which would
  // let the user click "Launch" with a reservationId that no longer matches the current inputs (the
  // backend then rejects it). Folding this into isEstimating closes that gap without any consumer
  // (Save button, skeleton) needing to know about it separately.
  const [isPendingEstimate, setIsPendingEstimate] = useState(false);

  const [createReservation, { isLoading: isCreating, reset: resetTokenReservationMutation }] =
    useCreateTokenReservationMutation();
  const [updateReservation, { isLoading: isUpdating, reset: resetUpdateReservation }] =
    useUpdateTokenReservationMutation();
  const dispatch = useAppDispatch();

  const didLaunchRef = useRef(false);
  const reservationIdRef = useRef<string | null>(null);

  // The estimate request currently on the wire. A newer estimate (or an unmount, or the form being
  // emptied) aborts it, so its result can never race or overwrite the latest one. We only ever call
  // `.abort()` on it, so the ref is kept to that minimal shape.
  const inFlightRef = useRef<{ abort: () => void } | null>(null);

  // Keep the ref in sync with state so the debounced estimate callback below reads the latest
  // reservation id synchronously when deciding between create and update.
  reservationIdRef.current = reservationId;

  const isReady = Boolean(dateRange?.from && dateRange?.to && assets.length > 0);

  const dateRangeStr = JSON.stringify(dateRange);

  // Reordering the same set must not re-trigger the debounced call. (We stringify so we can have a stable dependency)
  const assetsStr = JSON.stringify([...assets].sort((a, b) => a.localeCompare(b)));

  // Runs the actual network work for one estimate. It lives OUTSIDE the Effect Event (as a plain
  // async function) so the Effect Event itself can stay a thin, synchronous snapshot reader.
  const runEstimate = async (payload: GetTokenReservationPayload) => {
    // A newer estimate always supersedes an older one: abort whatever is still on the wire so its
    // result can never race or overwrite ours. RTK Query rejects the aborted call with an
    // `AbortError`, which we swallow in the catch below.
    inFlightRef.current?.abort();

    try {
      const reservationId = reservationIdRef.current;

      if (reservationId) {
        const request = updateReservation({ reservationId, payload });
        inFlightRef.current = request;
        const response = await request.unwrap();

        setEstimatedTokens(response.tokens);
        return;
      }

      const request = createReservation(payload);
      inFlightRef.current = request;
      const response = await request.unwrap();

      setReservationId(response.reservationId);
      setEstimatedTokens(response.tokens);
    } catch (err) {
      // We aborted this request on purpose (a newer cycle, an unmount, or the form being emptied);
      // it is not a real failure, so there is nothing to surface.
      if ((err as { name?: string })?.name === 'AbortError') {
        return;
      }

      const rtkError = err as RTKQueryError;
      const errorMsg = rtkError?.data?.message ?? 'Error estimating token reservation';

      if (!isUnreachableError(err)) {
        setReservationId(null);
      }

      setEstimatedTokens(null);
      setError(errorMsg);
      console.error('Failed to estimate:', rtkError);
    }
  };
  const startEstimate = () => {
    setError(null);
    const payload: GetTokenReservationPayload = {
      dateFrom: dayjs.utc(dateRange?.from).format('YYYY-MM-DD HH:mm:ss'),
      dateTo: dayjs.utc(dateRange?.to).format('YYYY-MM-DD HH:mm:ss'),
      application_instances: assets,
    };

    void runEstimate(payload);
  };

  const onFetchEstimate = useEffectEvent(() => {
    startEstimate();
  });

  useEffect(() => {
    //if the user has deselected all, we reset, abort any in-flight request, and we don't make the call
    if (!isReady) {
      inFlightRef.current?.abort();
      setEstimatedTokens(null);
      setError(null);
      setIsPendingEstimate(false);
      return;
    }

    // Mark the current reservation as stale the instant inputs change, not once the debounced call
    // actually fires. This is what keeps the Save button (and the skeleton) honest during the 600ms
    // debounce window.
    setIsPendingEstimate(true);

    const delayDebounceFn = setTimeout(() => {
      setIsPendingEstimate(false);
      onFetchEstimate();
    }, DEBOUNCED_INTERVAL_MS);

    return () => clearTimeout(delayDebounceFn);
  }, [dateRangeStr, assetsStr, isReady]);

  // Save the ID to our global Registry.
  // This ensures that if the user suddenly logs out, the Redux teardown process
  // can find and cancel this reservation before the Auth Token is destroyed.
  useEffect(() => {
    trackReservation(reservationId);
  }, [reservationId]);

  // The user closed the modal without clicking Launch. Abort any estimate still on the wire so a
  // late result can't fire after teardown, then let the registry cancel a reservation we already
  // confirmed. A request we abort before it returns hands us no id to cancel; if the backend still
  // committed that reservation, releasing it is the backend's responsibility (there is no FE id to
  // act on) -- we deliberately do not chase it.
  useEffect(() => {
    return () => {
      inFlightRef.current?.abort();
      if (!didLaunchRef.current) {
        void cancelTrackedReservation(dispatch);
      }
    };
  }, [dispatch]);

  // Lock it in: Once the investigation launches, stop tracking the reservation.
  // We don't want the cleanup function above (or a sudden logout) to accidentally
  // cancel a reservation that the backend is already actively consuming.
  const markAsLaunched = () => {
    didLaunchRef.current = true;
    trackReservation(null);
  };

  const resetAllMutations = () => {
    resetTokenReservationMutation?.();
    resetUpdateReservation?.();
  };

  return {
    estimatedTokens,
    reservationId,
    isEstimating: isPendingEstimate || isCreating || isUpdating,
    hasError: Boolean(error),
    errorMessage: error,
    markAsLaunched,
    resetAllMutations,
    retryEstimate: startEstimate,
  };
};
